import React, { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Lock,
  Mail,
  Eye,
  EyeOff,
  ChevronRight,
  ShieldCheck,
  Key,
  ArrowLeft,
  AlertCircle,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import { toast } from 'react-hot-toast';

import { logAuthEvent } from '../utils/sessionAudit';

import logoText from './impulza palabra fondo azul.png';

const getAuthFeedback = (error) => {
  const rawMessage = String(error?.message || '').trim();
  const message = rawMessage.toLowerCase();

  if (
    message.includes('invalid login credentials') ||
    message.includes('invalid credentials') ||
    message.includes('invalid email or password')
  ) {
    return {
      title: 'No pudimos iniciar sesión',
      message: 'El correo o la contraseña no coinciden. Revisá los datos e intentá nuevamente.',
    };
  }

  if (message.includes('email not confirmed') || message.includes('confirm')) {
    return {
      title: 'Cuenta pendiente de validación',
      message: 'La cuenta todavía no está confirmada. Revisá tu correo o consultá con el administrador.',
    };
  }

  if (message.includes('too many requests') || message.includes('rate limit')) {
    return {
      title: 'Demasiados intentos',
      message: 'Hiciste varios intentos seguidos. Esperá un momento antes de volver a probar.',
    };
  }

  if (message.includes('fetch') || message.includes('network') || message.includes('failed to fetch')) {
    return {
      title: 'Problema de conexión',
      message: 'No pudimos comunicarnos con el servidor. Verificá tu conexión e intentá de nuevo.',
    };
  }

  return {
    title: 'Error al iniciar sesión',
    message: rawMessage || 'Ocurrió un problema al validar tus credenciales. Intentá nuevamente.',
  };
};

// Cuanto esperamos a que AuthContext resuelva el profile antes de consultarlo
// por nuestra cuenta. Es una red de seguridad, no el camino normal.
const PROFILE_GATE_TIMEOUT_MS = 3500;

export default function Login() {
  const [view, setView] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [authFeedback, setAuthFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [installPrompt, setInstallPrompt] = useState(null);
  // Usuario que ya autentico y esta esperando el profile para saber si va al
  // ERP o a la pantalla de contrasena definitiva.
  const [pendingUserId, setPendingUserId] = useState(null);

  const navigate = useNavigate();
  const { profile: authProfile } = useAuth();
  const prefetchedSistemaRef = useRef(false);

  // Bajamos el chunk del ERP (~220 KB) mientras el usuario completa el
  // formulario, en vez de recien cuando navegamos. En mobile eso es la
  // diferencia entre ver el spinner un rato o entrar derecho.
  const prefetchSistema = useCallback(() => {
    if (prefetchedSistemaRef.current) return;
    prefetchedSistemaRef.current = true;
    import('./Sistema').catch(() => {
      // Si falla (red caida, chunk viejo post-deploy) no pasa nada: el lazy de
      // App.jsx lo vuelve a pedir con su propio retry.
      prefetchedSistemaRef.current = false;
    });
  }, []);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const clearFeedback = () => {
    if (authFeedback) setAuthFeedback(null);
  };

  // Decide el destino post-login una vez que sabemos si el usuario tiene que
  // cambiar la contrasena provisoria.
  const resolveProfileGate = useCallback((requiresPwUpdate) => {
    setPendingUserId(null);
    setLoading(false);

    if (requiresPwUpdate) {
      setView('set-password');
      setAuthFeedback({
        type: 'success',
        title: 'Primer ingreso detectado',
        message: 'Antes de continuar, configurá tu contraseña definitiva.',
      });
      toast('Configurá tu contraseña definitiva', { icon: '🔐' });
      return;
    }

    toast.success('Bienvenido a Impulza');
    navigate('/sistema');
  }, [navigate]);

  // El profile ya lo trae AuthContext al escuchar SIGNED_IN. Antes esta
  // pantalla hacia ademas su propio select('requires_pw_update'): un roundtrip
  // serial extra justo en el momento mas caro del flujo (mobile, red movil).
  // Ahora reusamos el del context y solo consultamos si tarda demasiado.
  useEffect(() => {
    if (!pendingUserId) return undefined;

    if (authProfile?.id === pendingUserId) {
      resolveProfileGate(!!authProfile.requires_pw_update);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('requires_pw_update')
          .eq('id', pendingUserId)
          .maybeSingle();
        resolveProfileGate(!!data?.requires_pw_update);
      } catch (e) {
        // Si tampoco podemos consultarlo, lo mandamos al ERP: ProtectedRoute y
        // AuthContext se encargan del resto. Peor que entrar es quedar colgado.
        console.warn('No pudimos verificar requires_pw_update, seguimos al ERP', e);
        resolveProfileGate(false);
      }
    }, PROFILE_GATE_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [pendingUserId, authProfile, resolveProfileGate]);

  const handleInstallClick = async () => {
    if (!installPrompt) return;

    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;

    if (outcome === 'accepted') {
      setInstallPrompt(null);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setAuthFeedback(null);

    try {
      let finalEmail = email.trim();
      if (finalEmail && !finalEmail.includes('@')) {
        finalEmail += '@usuarioimpulza.com.ar';
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email: finalEmail,
        password,
      });

      if (error) throw error;

      // Bitácora de sesiones (super admin): registramos el inicio. Best-effort,
      // no bloquea el ingreso si falla.
      logAuthEvent('login', { user: data.user });

      // Nos aseguramos de tener el chunk del ERP en camino aunque el usuario
      // haya llegado aca sin pasar por el foco del input (autofill, Enter).
      prefetchSistema();

      // El boton queda en loading hasta que el efecto de arriba resuelva el
      // destino con el profile del context.
      setPendingUserId(data.user.id);
    } catch (error) {
      const feedback = getAuthFeedback(error);
      setAuthFeedback({ type: 'error', ...feedback });
      toast.error(feedback.message, { id: 'login-error' });
      setLoading(false);
    }
  };

  const handleSetNewPassword = async (event) => {
    event.preventDefault();
    setAuthFeedback(null);

    if (newPassword !== confirmPassword) {
      const feedback = {
        type: 'error',
        title: 'Contraseñas distintas',
        message: 'La confirmación no coincide con la nueva contraseña.',
      };
      setAuthFeedback(feedback);
      toast.error(feedback.message, { id: 'set-password-error' });
      return;
    }

    if (newPassword.length < 6) {
      const feedback = {
        type: 'error',
        title: 'Contraseña muy corta',
        message: 'La nueva contraseña debe tener al menos 6 caracteres.',
      };
      setAuthFeedback(feedback);
      toast.error(feedback.message, { id: 'set-password-error' });
      return;
    }

    if (newPassword === password) {
      const feedback = {
        type: 'error',
        title: 'Contraseña repetida',
        message: 'La nueva contraseña debe ser diferente a la provisoria.',
      };
      setAuthFeedback(feedback);
      toast.error(feedback.message, { id: 'set-password-error' });
      return;
    }

    setLoading(true);
    try {
      const { error: authError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (authError) {
        if (authError.status === 422) {
          throw new Error('La contraseña no cumple los requisitos o es igual a la anterior.');
        }
        throw authError;
      }

      const currentUser = (await supabase.auth.getUser()).data.user;
      if (currentUser) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update({ requires_pw_update: false })
          .eq('id', currentUser.id);

        if (profileError) {
          console.error('No se pudo actualizar requires_pw_update en profiles', profileError);
        }
      }

      toast.success('Contraseña actualizada correctamente');
      navigate('/sistema');
    } catch (error) {
      const feedback = {
        type: 'error',
        title: 'No pudimos actualizar la contraseña',
        message: error?.message || 'Ocurrió un problema al guardar la nueva contraseña.',
      };
      setAuthFeedback(feedback);
      toast.error(feedback.message, { id: 'set-password-error' });
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const feedbackTone =
    authFeedback?.type === 'success'
      ? {
          wrap: 'bg-[#3FF2AA]/10 border-[#3FF2AA]/20 text-[#3FF2AA]',
          icon: <CheckCircle2 size={16} className="text-[#3FF2AA] shrink-0" />,
        }
      : {
          wrap: 'bg-rose-500/10 border-rose-500/20 text-rose-400',
          icon: <AlertCircle size={16} className="text-rose-400 shrink-0" />,
        };

  const inputClass = `w-full bg-[#081922] border pl-12 pr-4 py-3.5 rounded-xl text-xs font-bold text-white outline-none transition-all placeholder:text-slate-500 ${
    authFeedback?.type === 'error'
      ? 'border-rose-500/50 focus:border-rose-400 focus:ring-1 focus:ring-rose-400'
      : 'border-white/10 focus:border-[#3FF2AA] focus:ring-1 focus:ring-[#3FF2AA]'
  }`;

  return (
    <div className="min-h-screen bg-[#081922] text-white flex items-center justify-center p-4 font-sans selection:bg-[#3FF2AA]/30 relative overflow-hidden">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Space+Grotesk:wght@500;700;800&display=swap');
          .font-sans { font-family: 'Manrope', system-ui, sans-serif; }
          .imp-display { font-family: 'Space Grotesk', 'Manrope', sans-serif; }
        `}
      </style>

      {/* Decorative Blobs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#3FF2AA] blur-[150px] opacity-10 rounded-full pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#51A7FD] blur-[150px] opacity-10 rounded-full pointer-events-none"></div>

      <button
        onClick={() => navigate('/')}
        className="absolute top-6 left-6 md:top-8 md:left-8 flex items-center gap-2 font-semibold text-slate-400 hover:text-white transition-colors text-sm z-10"
      >
        <ArrowLeft size={18} /> Volver a la web
      </button>

      {installPrompt && (
        <button
          onClick={handleInstallClick}
          className="absolute top-6 right-6 md:top-8 md:right-8 flex items-center gap-2 font-bold bg-[#3FF2AA] text-[#081922] px-4 py-2 rounded-xl text-xs hover:scale-105 transition-transform shadow-lg shadow-[#3FF2AA]/20 z-10"
        >
          Instalar App
        </button>
      )}

      <div className="w-full max-w-[420px] animate-in fade-in zoom-in-95 duration-500 z-10 relative">
        <div className="flex flex-col items-center mb-10">
          <div
            className="flex items-center justify-center cursor-pointer hover:scale-105 transition-transform duration-500"
            onClick={() => navigate('/')}
          >
            <img
              src={logoText}
              alt="Impulza"
              className="h-9 md:h-10 w-auto object-contain"
            />
          </div>
          <p className="text-[10px] font-bold text-[#3FF2AA] uppercase tracking-[0.4em] mt-3 drop-shadow-[0_0_8px_rgba(63,242,170,0.3)]">
            Tu negocio simple
          </p>
        </div>

        <div className="bg-[#0B2A36] rounded-3xl border border-white/10 shadow-2xl p-8 md:p-10 relative overflow-hidden">
          
          <div className="text-center mb-8">
            <h2 className="imp-display text-2xl font-black text-white mb-1">
              {view === 'login' ? 'Iniciar Sesión' : 'Primer Ingreso'}
            </h2>
            <p className="text-xs text-slate-400 font-bold">
              {view === 'login' ? 'Accedé al panel de control de tu local' : 'Establece tu contraseña definitiva'}
            </p>
          </div>

          <form onSubmit={view === 'login' ? handleLogin : handleSetNewPassword} className="space-y-6">
            {authFeedback && (
              <div className={`rounded-xl border px-5 py-4 ${feedbackTone.wrap}`}>
                <div className="flex items-start gap-3">
                  {feedbackTone.icon}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest">{authFeedback.title}</p>
                    <p className="mt-1 text-xs font-medium leading-relaxed opacity-90">{authFeedback.message}</p>
                  </div>
                </div>
              </div>
            )}

            {view === 'login' ? (
              <>
                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-300 uppercase tracking-widest ml-1">
                    Correo electrónico
                  </label>
                  <div className="relative group">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#3FF2AA] transition-colors" size={18} />
                    <input
                      required
                      type="text"
                      autoComplete="username"
                      className={inputClass}
                      placeholder="usuario o correo"
                      value={email}
                      onFocus={prefetchSistema}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        clearFeedback();
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <label className="text-[10px] font-black text-slate-300 uppercase tracking-widest ml-1">
                    Contraseña
                  </label>
                  <div className="relative group">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#3FF2AA] transition-colors" size={18} />
                    <input
                      required
                      type={showPass ? 'text' : 'password'}
                      autoComplete="current-password"
                      className={inputClass}
                      placeholder="••••••••••••"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        clearFeedback();
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white transition-colors outline-none"
                    >
                      {showPass ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1.5 animate-in fade-in slide-in-from-right-4">
                  <label className="text-[10px] font-black text-slate-300 uppercase tracking-widest ml-1">
                    Nueva Contraseña
                  </label>
                  <div className="relative group">
                    <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#3FF2AA]" size={18} />
                    <input
                      required
                      type="password"
                      autoComplete="new-password"
                      className="w-full bg-[#081922] border border-white/10 pl-12 pr-4 py-3.5 rounded-xl text-xs font-bold text-white outline-none focus:border-[#3FF2AA] focus:ring-1 focus:ring-[#3FF2AA]"
                      placeholder="Mínimo 6 caracteres"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        clearFeedback();
                      }}
                    />
                  </div>
                </div>

                <div className="space-y-1.5 animate-in fade-in slide-in-from-right-4 delay-75">
                  <label className="text-[10px] font-black text-slate-300 uppercase tracking-widest ml-1">
                    Confirmar Contraseña
                  </label>
                  <div className="relative group">
                    <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-[#3FF2AA]" size={18} />
                    <input
                      required
                      type="password"
                      autoComplete="new-password"
                      className="w-full bg-[#081922] border border-white/10 pl-12 pr-4 py-3.5 rounded-xl text-xs font-bold text-white outline-none focus:border-[#3FF2AA] focus:ring-1 focus:ring-[#3FF2AA]"
                      placeholder="Repite la contraseña"
                      value={confirmPassword}
                      onChange={(e) => {
                        setConfirmPassword(e.target.value);
                        clearFeedback();
                      }}
                    />
                  </div>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full py-4 px-6 rounded-xl text-[13px] font-black uppercase tracking-wider flex items-center justify-center gap-2 transition-all shadow-lg outline-none
                ${loading ? 'opacity-70 cursor-not-allowed' : 'hover:scale-[1.02]'}
                ${view === 'login' 
                  ? 'bg-[#3FF2AA] text-[#081922] shadow-[#3FF2AA]/20 hover:bg-white' 
                  : 'bg-[#51A7FD] text-[#081922] shadow-[#51A7FD]/20 hover:bg-white'
                }
              `}
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : (
                 view === 'login' ? 'Ingresar a mi Negocio' : 'Actualizar Acceso'
              )}
              {!loading && <ChevronRight size={18} strokeWidth={3} />}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}
